// --- Logic --------------------------------------------------------------------------------------
export default {
    /** Already ends in `/admins` — pass only what comes after it to `useFetch`. */
    url: import.meta.env.VITE_API_URL,

    /** Mirrors the backend's `LANGUAGES`, in canonical order. */
    languages: ["ar", "en"] as const,
    /** Which of them render right to left. Read this instead of testing for `"ar"` by hand. */
    rtlLanguages: ["ar"] as const,

    /** Each language in its own words, never translated. */
    languageNames: { ar: "عربي", en: "English" } as Record<string, string>,

    /**
     * Mirrors the backend's `ADMIN_MODELS` — the segments under `/admins/`. A model missing from here
     * has no tab and no route, and the backend refuses the segment anyway.
     */
    models: ["home", "organizations", "subscriptions", "clients", "enquiries", "support", "admins"] as string[],
    /**
     * Mirrors the backend's `ADMIN_SUPER_MODELS` — reachable only by `admin.isSuper`.
     *
     * There are no admin roles. An admin either is super or is not, and the only thing that turns on
     * it is managing other admins: everything else about Lozicore's own team is shared work.
     */
    superModels: ["admins"] as string[],

    /**
     * The models with **no endpoints behind them yet**. A tab in this list is drawn with a marker and
     * its route renders `PendingScreen` instead of a screen.
     *
     * **Empty — every declared section now has an API and a screen.** It stays because it is the one
     * place that knows the build order: a sixth model goes in here on the day it gets a tab, and comes
     * out on the day its endpoints land.
     */
    pendingModels: [] as string[],

    /** Mirrors the backend's `MIN_PASSWORD_LENGTH` and `GENERATED_PASSWORD_LENGTH`. */
    minPasswordLength: 8,
    generatedPasswordLength: 8,

    /** Tabs the phone bar shows before the rest move behind "more" — five targets is the limit. */
    bottomBarTabs: 4,

    // --- Mirrored from the backend's `constants.ts` ----------------------------------------------

    /** The client roles inside one organization. Never invented here, never extended. */
    clientRoles: ["owner", "manager", "member"] as const,

    /** The periods we sell, and their list prices in SAR — the backend's `SUBSCRIPTION_PRICES`. */
    subscriptionPeriods: ["quarterly", "annually"] as const,
    subscriptionPrices: { quarterly: 750, annually: 1800 } as Record<string, number>,

    /** `SUBSCRIPTION_STATUSES`. `coming` is a paid renewal queued behind the live row. */
    subscriptionStatuses: ["pending", "active", "coming", "grace", "ended", "cancelled"] as const,

    /** `ENQUIRY_STATUSES`, in the order the queue is worked. The whole of what we track about one. */
    enquiryStatuses: ["new", "read", "done"] as const,

    /** `SUPPORT_STATUSES`, in the order the queue is worked. */
    supportStatuses: ["pending", "contacted", "solved"] as const,
    /** `SUPPORT_MESSAGE_MAX` — the ceiling on a reply, so the box can say when it is near it. */
    supportMessageMax: 2000,

    /** `PLAN_STORAGE_GB` — everything is unlimited on a paid plan except storage. */
    planStorageGb: 5,
    /** `STORAGE_ADDON` — stacks, full price whenever in the period it is bought, carried on renewal. */
    storageAddon: { gb: 5, quarterly: 100, annually: 240 },

    /** `FREE_LIMITS` — what an organization gets before it ever pays. Not a trial. */
    freeLimits: { clients: 1, projects: 2, units: 8, storageMb: 120 },

    /** `GRACE_WINDOW_DAYS` — full access after a lapse, before the organization goes read-only. */
    graceWindowDays: 5,
    /** `PENDING_PAYMENT_WINDOW_DAYS` — how long a request may wait for its transfer. */
    pendingPaymentWindowDays: 2,

    /** `CATALOG_THEMES`. The first entry is the default and the fallback. */
    catalogThemes: ["classic"] as const,

    /**
     * Where a company's public catalog answers: `{slug}.lozicore.com`. Written here rather than in
     * the copy, so the slug field's preview and the popup that explains it cannot print two
     * different addresses — and so a rename is one line.
     */
    catalogDomain: "lozicore.com",

    /**
     * `ENDING_SOON_DAYS` — how far ahead the subscriptions section's "ending" scope looks. The home
     * screen does not read this: its own figures are counted server-side, and the answer carries the
     * window it used, so the two can never disagree about what "soon" meant.
     */
    endingSoonDays: 14,

    /**
     * How many rows a list asks for by default, with the backend's own cap repeated so an input
     * cannot ask for a page the API will silently refuse — `hooks/usePageSize` owns the choice.
     */
    pageSize: 24,
    minPageSize: 5,
    maxPageSize: 100,

    /** The logo set in `public/logo`. */
    logo: {
        icon: "/logo/lozicore-icon.svg",
        round: "/logo/lozicore-round.svg",
        square: "/logo/lozicore-square.svg",
        circle: "/logo/lozicore-circle.svg",
    },
};
